import { useState, useRef, useEffect, useMemo } from 'react';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist/legacy/build/pdf.mjs';
import { X, CircleCheck, CircleAlert, RotateCcw } from 'lucide-react';
import api from '../../services/api';
import { lockScroll, unlockScroll } from '../../utils/scrollLock';
import styles from './ContractModal.module.css';

GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const PDF_URL         = '/pow-contract.pdf';
const TOTAL_PAGES     = 12;
const PDF_PAGE_HEIGHT = 841.92; // native page height in points

function todayStr() {
  const d = new Date();
  return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${d.getFullYear()}`;
}

/*
 * Positions derived from the raw PDF content stream (page height = 841.92 pts).
 * CSS y (top-down fraction) = (841.92 - pdf_y) / 841.92 - ~0.020 to sit above the underline.
 * Page 9  = CUSTOMER INFORMATION order form (pdf_y: 597–504)
 * Page 12 = CUSTOMER signature block      (pdf_y: 703–670)
 */
const buildFields = (fullName, email, date) => [
  /* ── Page 9: customer info ── */
  { key: 'name',        label: 'Full name',               type: 'text',  page: 9,  x: 0.32, y: 0.270, w: 0.56, h: 0.023, init: fullName  },
  { key: 'company',     label: 'Company (if applicable)', type: 'text',  page: 9,  x: 0.32, y: 0.292, w: 0.56, h: 0.023, init: '',       optional: true },
  { key: 'email',       label: 'Email address',           type: 'email', page: 9,  x: 0.32, y: 0.314, w: 0.56, h: 0.023, init: email     },
  { key: 'phone',       label: 'Telephone',               type: 'tel',   page: 9,  x: 0.28, y: 0.336, w: 0.56, h: 0.023, init: ''        },
  { key: 'address',     label: 'Mailing address',         type: 'text',  page: 9,  x: 0.28, y: 0.359, w: 0.56, h: 0.023, init: ''        },
  { key: 'title',       label: 'Effective date',          type: 'auto',  page: 9,  x: 0.28, y: 0.530, w: 0.64, h: 0.023, init: date,  readOnly: true },
  /* ── Page 9: Entry Fee Payment Method checkboxes (pdf_y ~397) ── */
  { key: 'payWire',      label: 'Wire Transfer', type: 'check', page: 10, x: 0.337, y: 0.256, w: 0.022, h: 0.018, init: '', group: 'payment', optional: true},
  { key: 'payCard',      label: 'Credit Card',   type: 'check', page: 10, x: 0.487, y: 0.256, w: 0.022, h: 0.018, init: '', group: 'payment', optional: true },
  { key: 'payOther',     label: 'Other',         type: 'check', page: 10, x: 0.618, y: 0.256, w: 0.022, h: 0.018, init: '', group: 'payment', optional: true },
  /* Only meaningful while "Other" is the selected method — dependsOn keeps it out
     of the Next-field walk, and clears it, whenever it isn't. */
  { key: 'payOtherText', label: 'Other (specify)', type: 'text', page: 10, x: 0.696, y: 0.256, w: 0.152, h: 0.018, init: '', optional: true, dependsOn: 'payOther' },
  /* ── Page 12: CUSTOMER signature block (pdf_y 703–670) ── */
  /* Runs from under the CUSTOMER: heading down to the Printed Name line (cap
     top 0.171), sitting on the printed signature rule at 0.164. The canvas is
     transparent, so the extra height above the rule costs no document text. */
  { key: 'signature',   label: 'Signature',               type: 'sig',   page: 12, x: 0.215, y: 0.128, w: 0.30, h: 0.043, init: ''        },
  { key: 'date',        label: 'Date',                    type: 'auto',  page: 12, x: 0.580, y: 0.140, w: 0.32, h: 0.026, init: date,  readOnly: true },
  { key: 'printedName', label: 'Printed name',            type: 'text',  page: 12, x: 0.225, y: 0.171, w: 0.32, h: 0.016, init: fullName  },
  { key: 'sigTitle',    label: 'Title',                   type: 'text',  page: 12, x: 0.175, y: 0.190, w: 0.52, h: 0.017, init: ''        },
  /* ── Page 12: POW LTD prefilled block (pdf_y 604–587) ── */
  { key: 'powDate', label: 'Date', type: 'auto', page: 12, x: 0.580, y: 0.241, w: 0.32, h: 0.026, init: date, readOnly: true },
];

/* A field the Next-field walk should pass over even though it is empty: an input
   whose dependency isn't met ("Other (specify)" while Wire Transfer is selected),
   or a radio-like group that already has a choice — picking Wire Transfer answers
   the payment-method question, so Credit Card and Other are not still pending. */
function isSatisfied(field, values, fields) {
  if (field.dependsOn && !values[field.dependsOn]) return true;
  if (field.group && fields.some(f => f.group === field.group && values[f.key])) return true;
  return false;
}

/* ── Signature canvas ── */
function SigCanvas({ onDraw, active, onActivate, value }) {
  const ref     = useRef(null);
  const drawing = useRef(false);
  const last    = useRef(null);

  function pt(e) {
    const canvas = ref.current;
    const rect   = canvas.getBoundingClientRect();
    const src    = e.touches?.[0] ?? e;
    return {
      x: (src.clientX - rect.left) * (canvas.width  / rect.width),
      y: (src.clientY - rect.top)  * (canvas.height / rect.height),
    };
  }

  function down(e) {
    e.preventDefault();
    if (!active) onActivate?.();
    drawing.current = true;
    last.current = pt(e);
  }
  function up()    { drawing.current = false; }
  function move(e) {
    e.preventDefault();
    if (!drawing.current) return;
    const p = pt(e);
    const ctx = ref.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.strokeStyle = '#111';
    ctx.lineWidth   = 1.5;
    ctx.lineCap     = 'round';
    ctx.stroke();
    last.current = p;
    onDraw(ref.current.toDataURL('image/png'));
  }
  function clear(e) {
    e.stopPropagation();
    const ctx = ref.current.getContext('2d');
    ctx.clearRect(0, 0, ref.current.width, ref.current.height);
    onDraw('');
  }

  return (
    <div className={styles.sigWrap}>
      <canvas
        ref={ref}
        width={480}
        height={90}
        className={`${styles.sigCanvas}${value ? '' : ` ${styles.sigCanvasEmpty}`}${active ? ` ${styles.sigCanvasActive}` : ''}`}
        onMouseDown={down} onMouseMove={move} onMouseUp={up} onMouseLeave={up}
        onTouchStart={down} onTouchMove={move} onTouchEnd={up}
      />
      {value && (
        <button className={styles.redoBtn} onMouseDown={clear} title="Clear and redo">
          <RotateCcw size={13} />
        </button>
      )}
    </div>
  );
}

const THUMB_SCALE = 0.13;

/* ── Sidebar page thumbnail ── */
function PdfThumb({ pdf, pageNum }) {
  const canvasRef = useRef(null);
  const taskRef   = useRef(null);

  useEffect(() => {
    if (!pdf || !canvasRef.current) return;
    let cancelled = false;
    (async () => {
      const page     = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: THUMB_SCALE });
      if (cancelled) return;
      const canvas   = canvasRef.current;
      canvas.width   = viewport.width;
      canvas.height  = viewport.height;
      if (taskRef.current) taskRef.current.cancel();
      taskRef.current = page.render({ canvasContext: canvas.getContext('2d'), viewport });
      try { await taskRef.current.promise; } catch (_) {}
    })();
    return () => { cancelled = true; taskRef.current?.cancel(); };
  }, [pdf, pageNum]);

  return <canvas ref={canvasRef} className={styles.thumbCanvas} />;
}

/* ── Single page renderer ── */
function PdfPage({ pdf, pageNum, scale, fields, values, activeKey, done, onFocus, onChange, inputRefs }) {
  const canvasRef = useRef(null);
  const taskRef   = useRef(null);

  useEffect(() => {
    if (!pdf || !canvasRef.current) return;
    let cancelled = false;
    (async () => {
      const page     = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale });
      if (cancelled) return;
      const canvas   = canvasRef.current;
      canvas.width   = viewport.width;
      canvas.height  = viewport.height;
      if (taskRef.current) taskRef.current.cancel();
      taskRef.current = page.render({ canvasContext: canvas.getContext('2d'), viewport });
      try { await taskRef.current.promise; } catch (_) {}
    })();
    return () => { cancelled = true; taskRef.current?.cancel(); };
  }, [pdf, pageNum, scale]);

  return (
    <div className={styles.pdfPage}>
      <canvas ref={canvasRef} />
      {/* Field overlay — only on the form page */}
      {fields.length > 0 && (
        <div className={styles.overlay}>
          {fields.map(f => {
            const isActive = f.key === activeKey;
            const isFilled = done.has(f.key);
            const val      = values[f.key];

            const pos = {
              position: 'absolute',
              left:   `${f.x * 100}%`,
              top:    `${f.y * 100}%`,
              width:  `${f.w * 100}%`,
              height: `${f.h * 100}%`,
            };

            if (f.type === 'sig') {
              return (
                <div key={f.key} style={{ ...pos, pointerEvents: 'auto' }}>
                  <SigCanvas
                    active={isActive}
                    onActivate={() => onFocus(f.key)}
                    onDraw={v => onChange(f.key, v)}
                    value={val}
                  />
                </div>
              );
            }

            if (f.type === 'check') {
              return (
                <div key={f.key} style={{ ...pos, pointerEvents: 'auto' }}>
                  <button
                    className={`${styles.checkBox} ${val ? styles.checkBoxChecked : ''}`}
                    onMouseDown={e => { e.preventDefault(); onChange(f.key, val ? '' : '✓', f.group); }}
                  />
                </div>
              );
            }

            /* "Other (specify)" only accepts input while Other is the selected
               payment method — otherwise it neither prompts nor takes focus. */
            const dormant = f.dependsOn && !values[f.dependsOn];

            return (
              <div key={f.key} style={pos}>
                <input
                  ref={el => { if (inputRefs) inputRefs.current[f.key] = el; }}
                  className={`${styles.docInput}${isActive && !dormant ? ` ${styles.docInputActive}` : ''}${isFilled && val ? ` ${styles.docInputFilled}` : ''}${f.bold ? ` ${styles.docInputBold}` : ''}`}
                  type={f.type === 'auto' ? 'text' : f.type}
                  value={val}
                  readOnly={!!f.readOnly || !!dormant}
                  tabIndex={dormant ? -1 : 0}
                  onFocus={() => { if (!f.readOnly && !dormant) onFocus(f.key); }}
                  onChange={e => onChange(f.key, e.target.value)}
                  placeholder={isActive && !dormant ? `Enter ${f.label.toLowerCase()}` : ''}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Modal ── */
function ContractModal({ onSigned }) {
  const { firstName = '', lastName = '', email = '' } =
    JSON.parse(localStorage.getItem('user') || '{}');
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  const signDate = todayStr();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const FIELDS = useMemo(() => buildFields(fullName, email, signDate), []);

  const [pdf,        setPdf]        = useState(null);
  const [scale,      setScale]      = useState(1.5);
  const [values,     setValues]     = useState(() => Object.fromEntries(FIELDS.map(f => [f.key, f.init])));
  const [done,       setDone]       = useState(() => {
    const s = new Set();
    FIELDS.forEach(f => {
      if (f.readOnly || f.type === 'auto' || f.init) s.add(f.key);
    });
    return s;
  });
  const [activeKey,  setActiveKey]  = useState(FIELDS[0].key);
  const [activePage, setActivePage] = useState(1);
  const [signed,     setSigned]     = useState(false);
  const [downloadUrl,setDownloadUrl]= useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitErr,  setSubmitErr]  = useState('');

  const scrollRef    = useRef(null);
  const pageRefs     = useRef({});
  const pdfAreaRef   = useRef(null);
  const inputRefs    = useRef({});
  const thumbRefs    = useRef({});
  const fieldBarRef  = useRef(null);

  /* Keep the active thumbnail in view as the main document scrolls */
  useEffect(() => {
    thumbRefs.current[activePage]?.scrollIntoView({ block: 'nearest' });
  }, [activePage]);

  /* Auto-focus the active input — preventScroll so the browser doesn't jump */
  useEffect(() => {
    const el = inputRefs.current[activeKey];
    if (el && document.activeElement !== el) el.focus({ preventScroll: true });
  }, [activeKey]);

  /* All interactive fields (for Next-field navigation) */
  const inputFields    = FIELDS.filter(f => f.type !== 'auto' && !f.readOnly);
  /* Only required fields count toward completion — grouped fields (e.g. payment
     method radios) are excluded here since the group-required gate below is
     disabled for now; `group` is still used for the sibling-uncheck (radio) UX. */
  const requiredFields  = inputFields.filter(f => !f.optional && !f.group);
  const requiredGroups  = []; // TODO: re-enable payment-method group requirement
  const unmetGroups     = requiredGroups.filter(g => !FIELDS.some(f => f.group === g && values[f.key]));
  const remaining       = requiredFields.filter(f => !done.has(f.key) || !values[f.key]).length + unmetGroups.length;
  const allDone         = remaining === 0;

  /* Group fields by their page number */
  const fieldsByPage = useMemo(() => {
    const m = {};
    FIELDS.forEach(f => {
      (m[f.page] = m[f.page] || []).push(f);
    });
    return m;
  }, [FIELDS]);

  /* Load PDF */
  useEffect(() => {
    getDocument({ url: PDF_URL }).promise.then(setPdf);
  }, []);

  /* Jump straight to the first field once the document has rendered,
     so it's in view immediately instead of silently focused off-screen. */
  useEffect(() => {
    if (!pdf || signed) return;
    const t = setTimeout(() => scrollToField(FIELDS[0], false), 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdf, signed]);

  /* Fit scale to the PDF area width; enforce a minimum on mobile so text stays readable */
  useEffect(() => {
    if (!pdfAreaRef.current) return;
    const obs = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width ?? 0;
      if (w > 0) {
        const natural = (w - 32) / 595.2;
        const minScale = w < 648 ? 1.2 : 1.0;
        setScale(Math.max(natural, minScale));
      }
    });
    obs.observe(pdfAreaRef.current);
    return () => obs.disconnect();
  }, []);

  /* Lock body scroll */
  useEffect(() => {
    lockScroll();
    return () => unlockScroll();
  }, []);

  /* Track visible page while scrolling */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const midY = el.scrollTop + el.clientHeight / 2;
      let best = 1;
      for (let p = 1; p <= TOTAL_PAGES; p++) {
        const ref = pageRefs.current[p];
        if (ref && ref.offsetTop <= midY) best = p;
      }
      setActivePage(best);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  function scrollToPage(p) {
    const el = pageRefs.current[p];
    if (el && scrollRef.current) {
      scrollRef.current.scrollTo({ top: el.offsetTop - 16, behavior: 'smooth' });
    }
    setActivePage(p);
  }

  function scrollToField(field, smooth = true) {
    const pageEl = pageRefs.current[field.page];
    if (!pageEl || !scrollRef.current) return;
    /* Derive canvas height from scale directly — avoids relying on the
       canvas having already finished its async pdfjs render. */
    const canvasH  = PDF_PAGE_HEIGHT * scale;
    const fieldTop = pageEl.offsetTop + field.y * canvasH;
    const barH     = fieldBarRef.current?.offsetHeight ?? 0;
    const margin   = 24;
    scrollRef.current.scrollTo({ top: fieldTop - barH - margin, behavior: smooth ? 'smooth' : 'auto' });
    setActivePage(field.page);
  }

  function onFocus(key) {
    setActiveKey(key);
    const field = FIELDS.find(f => f.key === key);
    if (field) scrollToField(field);
  }

  function onChange(key, val, group) {
    const next = { ...values, [key]: val };
    /* Radio-like: uncheck siblings in the same group */
    if (group && val) {
      FIELDS.forEach(f => { if (f.group === group && f.key !== key) next[f.key] = ''; });
    }
    /* A dependent input belongs to the option it hangs off: switching the payment
       method away from Other drops whatever was typed into "Other (specify)",
       rather than leaving a stale value to be written onto the contract. */
    FIELDS.forEach(f => { if (f.dependsOn && !next[f.dependsOn]) next[f.key] = ''; });

    setValues(next);
    setDone(prev => {
      const n = new Set(prev);
      if (val) n.add(key);
      /* Anything left empty by the above is no longer a completed field */
      FIELDS.forEach(f => { if (!next[f.key]) n.delete(f.key); });
      return n;
    });
  }

  function nextField() {
    /* Mark current filled */
    if (values[activeKey]) setDone(prev => new Set([...prev, activeKey]));

    const currIdx = inputFields.findIndex(f => f.key === activeKey);
    /* Find the next field still awaiting an answer */
    let next = currIdx + 1;
    while (
      next < inputFields.length &&
      ((done.has(inputFields[next].key) && values[inputFields[next].key]) ||
       isSatisfied(inputFields[next], values, FIELDS))
    ) {
      next++;
    }
    if (next < inputFields.length) {
      const nf = inputFields[next];
      setActiveKey(nf.key);
      scrollToField(nf);
    }
  }

  async function submit() {
    if (!allDone || submitting) return;
    setSubmitting(true);
    setSubmitErr('');
    try {
      const { data } = await api.post('/contracts/sign', {
        name:           values.name,
        company:        values.company,
        email:          values.email,
        phone:          values.phone,
        address:        values.address,
        title:          values.title,
        printedName:    values.printedName,
        signatureTitle: values.sigTitle,
        signatureData:  values.signature,
        signedDate:     signDate,
        paymentMethod:  values.payWire ? 'Wire Transfer' : values.payCard ? 'Credit Card' : values.payOther ? `Other: ${values.payOtherText || ''}` : '',
      });
      setDownloadUrl(data.downloadUrl);
      setSigned(true);
      /* Persist to localStorage so DashboardLayout doesn't re-show the modal */
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...u, contractSigned: true, contractSignedAt: new Date().toISOString() }));
    } catch (err) {
      setSubmitErr(err.response?.data?.error || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDownload() {
    try {
      const res  = await fetch(downloadUrl);
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = 'POW-DFY-Agreement-Signed.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      window.open(downloadUrl, '_blank');
    }
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>

        {/* ── Header ── */}
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <h2 className={styles.title}>DFY Program Terms and Conditions</h2>
            {signed && (
              <button className={styles.closeBtn} onClick={onSigned} aria-label="Close">
                <X size={18} />
              </button>
            )}
          </div>

          <div className={`${styles.alert} ${signed ? styles.alertSuccess : styles.alertWarn}`}>
            {signed
              ? <CircleCheck size={15} className={styles.alertIcon} />
              : <CircleAlert size={15} className={styles.alertIcon} />}
            <span className={styles.alertText}>
              {signed
                ? 'Signature successfully submitted!'
                : 'Fill and sign the document below to get access to your portal.'}
            </span>
            {/* Desktop download button — hidden on mobile (moves to bottom bar) */}
            {signed && (
              <button className={`${styles.downloadBtn} ${styles.downloadBtnDesktop}`} onClick={handleDownload}>
                Download signed copy
              </button>
            )}
          </div>

          {submitErr && <p className={styles.submitErr}>{submitErr}</p>}
        </div>

        {/* ── Body ── */}
        <div className={styles.body}>
          {/* Thumbnail sidebar — hidden on mobile */}
          <div className={styles.thumbSidebar}>
            {Array.from({ length: TOTAL_PAGES }, (_, i) => i + 1).map(p => (
              <button
                key={p}
                ref={el => { if (el) thumbRefs.current[p] = el; }}
                className={`${styles.thumb} ${activePage === p ? styles.thumbActive : ''}`}
                onClick={() => scrollToPage(p)}
              >
                <div className={styles.thumbPage}>
                  {pdf && <PdfThumb pdf={pdf} pageNum={p} />}
                </div>
                <span className={styles.thumbNum}>{p}</span>
              </button>
            ))}
          </div>

          {/* Scrollable PDF */}
          <div className={styles.pdfScroll} ref={scrollRef}>
            {/* Desktop field bar — hidden on mobile, lives inside the document container */}
            {!signed && (
              <div className={styles.fieldBar} ref={fieldBarRef}>
                <span className={styles.fieldCount}>
                  {allDone
                    ? 'All required fields completed'
                    : <><strong className={styles.fieldCountNum}>{remaining}</strong> required field{remaining !== 1 ? 's' : ''} left</>}
                </span>
                {allDone ? (
                  <button className={`${styles.ctaBtn} ${styles.ctaSubmit}`} onClick={submit} disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Submit'}
                  </button>
                ) : (
                  <button className={styles.ctaBtn} onClick={nextField}>Next field</button>
                )}
              </div>
            )}

            {/* Mobile count bar — shown only on mobile, above PDF */}
            {!signed && (
              <div className={styles.mobileCountBar}>
                {allDone ? (
                  <span className={styles.mobileCountDone}>All required fields completed</span>
                ) : (
                  <span className={styles.mobileCountText}>
                    <strong className={styles.mobileCountNum}>{remaining}</strong> required field{remaining !== 1 ? 's' : ''} left
                  </span>
                )}
              </div>
            )}

            <div className={styles.pdfInner} ref={pdfAreaRef}>
              {Array.from({ length: TOTAL_PAGES }, (_, i) => i + 1).map(p => (
                <div
                  key={p}
                  ref={el => { if (el) pageRefs.current[p] = el; }}
                  className={styles.pdfPageWrapper}
                >
                  <PdfPage
                    pdf={pdf}
                    pageNum={p}
                    scale={scale}
                    fields={!signed ? (fieldsByPage[p] || []) : []}
                    values={values}
                    activeKey={activeKey}
                    done={done}
                    onFocus={onFocus}
                    onChange={onChange}
                    inputRefs={inputRefs}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Mobile sticky bottom bar ── */}
        <div className={styles.mobileCta}>
          {signed ? (
            <>
              <button className={`${styles.mobileCtaBtn} ${styles.mobileCtaSubmit}`} onClick={handleDownload}>
                Download signed copy
              </button>
              <button className={styles.mobileCtaDone} onClick={onSigned}>
                Done
              </button>
            </>
          ) : allDone ? (
            <button className={`${styles.mobileCtaBtn} ${styles.mobileCtaSubmit}`} onClick={submit} disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit'}
            </button>
          ) : (
            <button className={styles.mobileCtaBtn} onClick={nextField}>
              Next field
            </button>
          )}
          {submitErr && <p className={styles.mobileSubmitErr}>{submitErr}</p>}
        </div>

      </div>
    </div>
  );
}

export default ContractModal;
